import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import anime from 'animejs';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import InsuranceCard from './InsuranceCard';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import {
  IMAGE_TYPE_FRONT,
  IMAGE_TYPE_BACK,
  ENTER_MANUALLY_FROM_TIMEOUT,
  SHOW_TAKING_TOO_LONG,
} from '../../constants/index';
import InsuranceCardShape from '../../shapes/InsuranceCard';
import { analyticsTrackEvent } from '../../core/analytics';
import MainCtaButton from './components/MainCtaButton';

const TAKE_TOO_LONG_TIMEOUT = 30000; // 30 seconds in milliseconds

const StyledSkipInsuranceButton = styled(Button)`
  margin-top: 5px;
  margin-left: auto;
  margin-right: auto;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;
`;

const CtaContainerGrid = styled(Grid)`
  margin-top: 15px;
`;

const ANIMATION_CLASS_NAME = 'anime-target';

class MobileAddInsurance extends PureComponent {
  static propTypes = {
    insuranceCard: InsuranceCardShape,
    onClickFront: PropTypes.func,
    onClickBack: PropTypes.func,
    handleFileChange: PropTypes.func,
    handleEnterManually: PropTypes.func,
    handleSkipBack: PropTypes.func,
    skipBack: PropTypes.bool,
    handleSkipInsurance: PropTypes.func,
    className: PropTypes.string,
  };

  static defaultProps = {
    insuranceCard: {
      front: {},
      back: {},
    },
  };

  state = {
    showTakeTooLong: false,
  };

  componentDidUpdate = prevProps => {
    const { insuranceCard, skipBack } = this.props;
    const prevStarted = this.isScanStarted(prevProps);
    const nowStarted = this.isScanStarted(this.props);

    if (!prevStarted && nowStarted) {
      setTimeout(() => {
        this.setState({ showTakeTooLong: true });
        analyticsTrackEvent(SHOW_TAKING_TOO_LONG);
      }, TAKE_TOO_LONG_TIMEOUT);
    }

    if (skipBack) return;
    if (!prevProps.insuranceCard.back.scanStarted && insuranceCard.back.scanStarted) {
      setTimeout(
        () =>
          anime({
            targets: `.${ANIMATION_CLASS_NAME}`,
            translateX: [-328, 0],
            direction: 'alternate',
            loop: true,
            easing: 'linear',
            duration: 3000,
          }),
        500
      );
    }
  };

  isFrontDisabled = () => {
    const { insuranceCard, skipBack } = this.props;
    if (skipBack) return false;
    return insuranceCard.front.base64 && !insuranceCard.back.base64;
  };

  isFrontScanCompleted = () => {
    const {
      skipBack,
      insuranceCard: {
        front: { scanCompleted: frontScanCompleted },
        back: { scanCompleted: backScanCompleted },
      },
    } = this.props;

    return skipBack ? frontScanCompleted : frontScanCompleted && backScanCompleted;
  };

  isScanStarted = ({
    skipBack,
    insuranceCard: {
      front: { scanStarted: frontScanStarted },
      back: { scanStarted: backScanStarted },
    },
  }) => (skipBack ? frontScanStarted : frontScanStarted && backScanStarted);

  render = () => {
    const {
      insuranceCard,
      onClickBack,
      onClickFront,
      handleFileChange,
      skipBack,
      handleSkipInsurance,
      handleEnterManually,
      handleSkipBack,
      className,
    } = this.props;
    return (
      <Fragment>
        <Grid container justify="center" className={className}>
          <InsuranceCard
            cardImage={insuranceCard.front.base64}
            disabled={this.isFrontDisabled()}
            onClick={onClickFront}
            scanCompleted={this.isFrontScanCompleted()}
            scanStarted={insuranceCard.front.scanStarted}
            handleFileChange={handleFileChange(IMAGE_TYPE_FRONT)}
            animationClassName={ANIMATION_CLASS_NAME}
            parentControlAnimation={!skipBack}
            placeholderImage="/images/insurance/front_copy.svg"
            placeholderCopy={
              <Fragment>
                Place card on a flat, well lit surface
                <br />
                (no glare) and tap button below.
              </Fragment>
            }
            showPlaceholder
          >
            Front of card
          </InsuranceCard>
        </Grid>
        {!skipBack && (
          <Grid container justify="center" className={className}>
            <InsuranceCard
              cardImage={insuranceCard.back.base64}
              showPlaceholder={Boolean(insuranceCard.front.base64)}
              onClick={onClickBack}
              scanCompleted={insuranceCard.back.scanCompleted && insuranceCard.front.scanCompleted}
              scanStarted={insuranceCard.front.scanStarted && insuranceCard.back.scanStarted}
              handleFileChange={handleFileChange(IMAGE_TYPE_BACK)}
              buttonStyle={ButtonVariations.tertiary}
              animationClassName={ANIMATION_CLASS_NAME}
              placeholderCopy="Flip card over and tap button below to capture the back of card."
              placeholderImage="/images/insurance/back_copy.svg"
              parentControlAnimation
            >
              Back of card
            </InsuranceCard>
          </Grid>
        )}
        <CtaContainerGrid xs={12} item className={className}>
          <Grid container justify="center">
            <MainCtaButton
              handleEnterManually={handleEnterManually}
              handleSkipBack={handleSkipBack}
              insuranceCard={insuranceCard}
              skipBack={skipBack}
            />
          </Grid>
          <Grid container justify="center">
            {this.state.showTakeTooLong && (
              <StyledSkipInsuranceButton
                variation={ButtonVariations.link}
                onClick={() => handleEnterManually({ from: ENTER_MANUALLY_FROM_TIMEOUT })}
              >
                Taking too long? Enter manually
              </StyledSkipInsuranceButton>
            )}
          </Grid>
        </CtaContainerGrid>
      </Fragment>
    );
  };
}

export default MobileAddInsurance;
